import { useEffect } from "react";

/**
 * Small SEO / social-preview helper for our SPA.
 *
 * On mount, updates the document <title> and the meta / og / twitter tags to
 * page-specific values. On unmount, restores the site-wide defaults declared
 * in /app/frontend/public/index.html so that navigating back to any un-tagged
 * page (Home, Contact, etc.) shows the correct site-level metadata.
 *
 * Only touches metadata — no visual changes, no re-renders triggered.
 */

const DEFAULTS = {
    title: "ZSGI | School of Global Impact",
    description:
        "School of Global Impact (ZSGI), creating global programmes in leadership, sustainability, education and international engagement.",
    ogTitle: "ZSGI | School of Global Impact",
    ogDescription:
        "Global programmes, conferences and opportunities by the School of Global Impact.",
    twitterTitle: "ZSGI | School of Global Impact",
    twitterDescription:
        "Global programmes, conferences and opportunities by the School of Global Impact.",
    canonical: "https://zsgi.org/",
};

function setMeta(selector, attr, value) {
    if (!value) return;
    const el = document.querySelector(selector);
    if (el) el.setAttribute(attr, value);
}

function setCanonical(href) {
    if (!href) return;
    let el = document.querySelector('link[rel="canonical"]');
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", "canonical");
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

export function usePageMeta({ title, description, ogTitle, ogDescription, twitterTitle, twitterDescription, canonical } = {}) {
    useEffect(() => {
        const prev = {
            title: document.title,
            description: document.querySelector('meta[name="description"]')?.getAttribute("content"),
            ogTitle: document.querySelector('meta[property="og:title"]')?.getAttribute("content"),
            ogDescription: document.querySelector('meta[property="og:description"]')?.getAttribute("content"),
            twitterTitle: document.querySelector('meta[name="twitter:title"]')?.getAttribute("content"),
            twitterDescription: document.querySelector('meta[name="twitter:description"]')?.getAttribute("content"),
            canonical: document.querySelector('link[rel="canonical"]')?.getAttribute("href"),
        };

        if (title) document.title = title;
        setMeta('meta[name="description"]', "content", description);
        setMeta('meta[property="og:title"]', "content", ogTitle);
        setMeta('meta[property="og:description"]', "content", ogDescription);
        setMeta('meta[name="twitter:title"]', "content", twitterTitle);
        setMeta('meta[name="twitter:description"]', "content", twitterDescription);
        if (canonical) setCanonical(canonical);

        return () => {
            // Restore site-wide defaults on unmount so the next route inherits
            // clean metadata even if it doesn't call usePageMeta itself.
            document.title = prev.title || DEFAULTS.title;
            setMeta('meta[name="description"]', "content", prev.description || DEFAULTS.description);
            setMeta('meta[property="og:title"]', "content", prev.ogTitle || DEFAULTS.ogTitle);
            setMeta('meta[property="og:description"]', "content", prev.ogDescription || DEFAULTS.ogDescription);
            setMeta('meta[name="twitter:title"]', "content", prev.twitterTitle || DEFAULTS.twitterTitle);
            setMeta('meta[name="twitter:description"]', "content", prev.twitterDescription || DEFAULTS.twitterDescription);
            if (prev.canonical) setCanonical(prev.canonical);
        };
    }, [title, description, ogTitle, ogDescription, twitterTitle, twitterDescription, canonical]);
}
